import {
    cocktailPrice,
    getCocktailProduct,
    getProductQuantity,
    getProductType,
    isBeerProduct,
    isSparklingWineProduct,
    isWaterProduct,
    isWineProduct,
} from "@/helpers/quoteFunctions";
import React, { useState } from "react";
import { FiTrash2, FiEdit2 } from "react-icons/fi";

/** When both cocktails and products exist: cocktail lines share 80%, product lines share 20% (10% wines, 10% beers). Sparkling wine is separate at 5%. */
const COCKTAIL_SHARE_PERCENT = 80;
const PRODUCT_SHARE_PERCENT = 20;
const WINE_SHARE_PERCENT = 10;
const BEER_SHARE_PERCENT = 10;
const SPARKLING_WINE_SHARE_PERCENT = 5;

export type CocktailItemProps = {
    id: string;
    provider?: any | "";
    type?: any | "";
    cocktails?: any | "";
    brand?: any | "";
    brandName?: any | "";
    qty?: number | string | "";
    pricePerItem?: number | string | "";
};

const isPreferredBrand = (brand: any, preferredProducts: any[]): boolean => {
    const productId = brand?.product?.id ?? brand?.id ?? brand?.value;
    if (!productId) return false;
    return preferredProducts.some((p: any) => p.prefer1?.id === productId);
};

/** Group key: same cocktail (by cocktails.id) = one group; custom cocktails and products = one item per group */
function getGroupKey(it: CocktailItemProps): string {
    if (it?.type?.type === "cocktails" && it?.cocktails?.id != null && it?.cocktails?.value !== "custom") {
        return `cocktail-${it.cocktails.id}`;
    }
    return `product-${it.id}`;
}

function groupItems(items: CocktailItemProps[]): { key: string; items: CocktailItemProps[]; indices: number[] }[] {
    const map = new Map<string, { items: CocktailItemProps[]; indices: number[] }>();
    items.forEach((it, index) => {
        const key = getGroupKey(it);
        const existing = map.get(key);
        if (existing) {
            existing.items.push(it);
            existing.indices.push(index);
        } else {
            map.set(key, { items: [it], indices: [index] });
        }
    });
    return Array.from(map.entries()).map(([key, { items: groupItems, indices }]) => ({
        key,
        items: groupItems,
        indices,
    }));
}

type Props = {
    items: CocktailItemProps[];
    onAddClick: () => void;
    onEdit: (id: string) => void;
    onCancelEdit: () => void;
    onRemove: (id: string) => void;
    /** When removing a group (e.g. same cocktail with multiple ingredients), pass all ids to remove in one go */
    onRemoveGroup?: (ids: string[]) => void;
    onQtyChange?: (index: number, qty: number) => void;
    currency?: string; // default USD
    className?: string;
    editIndex?: number | null;
    numberOfGuests: number;
    estDrinks: number;
    isAddDisabled?: boolean;
    preferredProducts?: any[];
};

export default function CocktailItemsList({
    items,
    onAddClick,
    onEdit,
    onRemove,
    onRemoveGroup,
    onQtyChange,
    currency = "USD",
    className = "",
    editIndex,
    onCancelEdit,
    numberOfGuests,
    estDrinks,
    isAddDisabled,
    preferredProducts = [],
}: Props) {
    const groups = groupItems(items);
    // Local state for qty input so typing doesn't trigger parent re-renders and lose focus
    const [editingQty, setEditingQty] = useState<{ index: number; value: string } | null>(null);

    // Suggested quantity per line based on current config (mirrors QuoteBuilder allocation logic)
    const productCount = items.filter((f) => f.type?.type === "products").length;
    const cocktailCount = items.filter((f) => f.type?.type === "cocktails").length;
    const wineProductCount = items.filter((f) => f.type?.type === "products" && isWineProduct(f.type?.value)).length;
    const beerProductCount = items.filter((f) => f.type?.type === "products" && isBeerProduct(f.type?.value)).length;
    const waterProductCount = items.filter((f) => f.type?.type === "products" && isWaterProduct(f.type?.value)).length;
    const hasBoth = productCount > 0 && cocktailCount > 0;
    const effectiveEstDrinksCocktails = hasBoth ? estDrinks * 0.8 : estDrinks;
    const cocktailLineCount = cocktailCount || 1;
    const numberOfCocktailsPerLine = Math.max(
        1,
        Math.ceil(effectiveEstDrinksCocktails / cocktailLineCount)
    );

    // Product-side effective drinks split, copied from QuoteBuilder
    const effectiveEstDrinksWines = hasBoth
        ? (beerProductCount > 0 ? estDrinks * 0.1 : estDrinks * 0.2)
        : beerProductCount > 0
            ? estDrinks * 0.5
            : estDrinks;
    const effectiveEstDrinksBeers = hasBoth
        ? (wineProductCount > 0 ? estDrinks * 0.1 : estDrinks * 0.2)
        : wineProductCount > 0
            ? estDrinks * 0.5
            : estDrinks;
    const effectiveEstDrinksOtherProducts = hasBoth ? estDrinks * 0.2 : estDrinks;

    // Percentage breakdown: cocktails 80%; within products 20% → 10% wines + 10% beers when both exist; else full 20% to the one present
    const wineShareTotal = hasBoth ? (beerProductCount > 0 ? WINE_SHARE_PERCENT : PRODUCT_SHARE_PERCENT) : 100;
    const beerShareTotal = hasBoth ? (wineProductCount > 0 ? BEER_SHARE_PERCENT : PRODUCT_SHARE_PERCENT) : 100;

    const getProductEffectiveDrinks = (productValue: string | undefined) => {
        if (isWineProduct(productValue))
            return { effective: effectiveEstDrinksWines, lineCount: wineProductCount || 1 };
        if (isBeerProduct(productValue))
            return { effective: effectiveEstDrinksBeers, lineCount: beerProductCount || 1 };
        // water and other products share the generic 20% (or 100% when only products)
        return { effective: effectiveEstDrinksOtherProducts, lineCount: productCount || 1 };
    };

    const getLineSharePercent = (it: CocktailItemProps): number | null => {
        if (hasBoth) {
            if (it?.type?.type === "cocktails") {
                const n = cocktailCount || 1;
                return Math.round((COCKTAIL_SHARE_PERCENT / n) * 10) / 10; // e.g. 26.7 for 3
            }
            if (it?.type?.type === "products") {
                const value = it?.type?.value;
                if (isWineProduct(value)) {
                    const n = wineProductCount || 1;
                    return Math.round((wineShareTotal / n) * 10) / 10; // 10% or 20% total split among wines
                }
                if (isBeerProduct(value)) {
                    const n = beerProductCount || 1;
                    return Math.round((beerShareTotal / n) * 10) / 10; // 10% or 20% total split among beers
                }
                // water: 1.5 per guest, not part of percentage allocation
                if (isWaterProduct(value)) return null;
                // other products: no share of the 10/10 split
                return null;
            }
        }
        if (it?.type?.type === "cocktails") {
            const n = cocktailCount || 1;
            return Math.round((100 / n) * 10) / 10;
        }
        if (it?.type?.type === "products") {
            const value = it?.type?.value;
            if (isWaterProduct(value)) return null; // water: 1.5 per guest, not part of allocation

            // Products only (no cocktails):
            // - If both beer and wine exist: 50% total to beers, 50% total to wines.
            // - If only one of beer or wine exists: that category gets 100%.
            // - Other products only (no beer/wine): split 100% across them.
            if (cocktailCount === 0) {
                const hasWine = wineProductCount > 0;
                const hasBeer = beerProductCount > 0;

                if (isWineProduct(value)) {
                    const groupTotal = hasWine && hasBeer ? 50 : 100;
                    const n = wineProductCount || 1;
                    return Math.round((groupTotal / n) * 10) / 10;
                }
                if (isBeerProduct(value)) {
                    const groupTotal = hasWine && hasBeer ? 50 : 100;
                    const n = beerProductCount || 1;
                    return Math.round((groupTotal / n) * 10) / 10;
                }

                // If there are no beer/wine products, share 100% across remaining products (excluding water)
                if (!hasWine && !hasBeer) {
                    const otherProductCount = productCount - waterProductCount || 1;
                    return Math.round((100 / otherProductCount) * 10) / 10;
                }

                // If beer/wine exist, but this product is "other", it doesn't participate in the 50/50 split
                return null;
            }

            // Cocktails + products: for non-water products, 100% is already handled by wines/beers
            const otherProductCount = productCount - waterProductCount || 1;
            return Math.round((100 / otherProductCount) * 10) / 10;
        }
        return null;
    };

    // Label for the percentage line (e.g. "26.7% of cocktails (80% total)", "20% of beers (20% total)" when no wines)
    const getLineShareLabel = (it: CocktailItemProps): string | null => {
        const pct = getLineSharePercent(it);
        if (pct == null) return null;
        if (hasBoth) {
            if (it?.type?.type === "cocktails") {
                return `${pct}% of cocktails (80% total)`;
            }
            if (it?.type?.type === "products") {
                if (isWineProduct(it?.type?.value))
                    return `${pct}% of wines (${wineShareTotal}% total)`;
                if (isBeerProduct(it?.type?.value))
                    return `${pct}% of beers (${beerShareTotal}% total)`;
                return `${pct}% of products (20% total)`;
            }
        }
        if (it?.type?.type === "products" && isSparklingWineProduct(it?.type?.value))
            return `${pct}% of sparkling wine (5% total)`;
        return `${pct}% of drinks`;
    };

    // Display actual estimated drinks for this line (derived from percentage); water uses 1.5 per guest, not %
    const getLineShareActualLabel = (it: CocktailItemProps): string | null => {
        if (it?.type?.type === "products" && isWaterProduct(it?.type?.value)) {
            const qty = Math.max(
                1,
                Math.ceil(
                    getProductQuantity(
                        it.brand,
                        it.type?.value,
                        numberOfGuests,
                        1,
                        estDrinks
                    )
                )
            );
            return ``;
            // return `${qty} drinks (1.5 per guest)`;
        }
        const pct = getLineSharePercent(it);
        if (pct == null) return null;
        const actualDrinks = Math.round((Number(estDrinks) || 0) * (pct / 100));
        return `${actualDrinks} drinks`;
    };

    return (
        <div className={className}>
            {/* Save & Add (dashed pill) */}
            {editIndex !== null ? (
                <div className="flex flex-row gap-3">
                    <button
                        type="button"
                        disabled={!!isAddDisabled}
                        onClick={onAddClick}
                        className="w-full rounded-2xl font-[400] border border-dashed border-[#698293] bg-[#F7F8FA] py-3 text-xs text-[#282828] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#F7F8FA]"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={onCancelEdit}
                        className="w-full rounded-2xl font-[400] border border-dashed border-[#698293] bg-[#F7F8FA] py-3 text-xs text-[#282828] hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <button
                    disabled={!!isAddDisabled}
                    type="button"
                    onClick={onAddClick}
                    className="w-full rounded-2xl font-[400] border border-dashed border-[#698293] bg-[#F7F8FA] py-3 text-xs text-[#282828] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#F7F8FA]"
                >
                    + Save and Add
                </button>
            )}

            {/* Items: grouped by same cocktail (no inner borders); one edit/delete per group */}
            <ul className="mt-5 ">
                {groups.map((group, groupIndex) => {
                    const groupTotalDrinks =
                        group.items.length > 1
                            ? group.items.reduce((sum, item) => {
                                  const pct = getLineSharePercent(item);
                                  if (pct == null) return sum;
                                  return sum + Math.round((Number(estDrinks) || 0) * (pct / 100));
                              }, 0)
                            : null;
                    return (
                    <li
                        key={group.key}
                        className={`border-b border-gray-200 ${groupIndex === 0 ? "border-t border-gray-200" : ""}`}
                    >
                        <div className="w-full overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]">
                            {group.items.map((it, subIndex) => {
                                const index = group.indices[subIndex];
                                const isFirstInGroup = subIndex === 0;
                                const showActions = isFirstInGroup;
                                const handleRemove = () => {
                                    if (group.items.length > 1 && onRemoveGroup) {
                                        onRemoveGroup(group.items.map((i) => i.id));
                                    } else {
                                        onRemove(it.id);
                                    }
                                };
                                let suggestedQty: number | null = null;
                                if (it?.type?.type === "products") {
                                    const { effective, lineCount } = getProductEffectiveDrinks(it.type?.value);
                                    const raw = getProductQuantity(
                                        it.brand,
                                        it.type?.value,
                                        numberOfGuests,
                                        lineCount,
                                        effective
                                    );
                                    suggestedQty = Math.max(1, Math.ceil(raw));
                                } else if (it?.brand) {
                                    const qtyResult = getCocktailProduct(
                                        it.brand,
                                        numberOfCocktailsPerLine,
                                        it?.cocktails?.id
                                    );
                                    suggestedQty =
                                        typeof qtyResult?.quantity === "number"
                                            ? qtyResult.quantity
                                            : null;
                                }
                                const suggestedNode =
                                    typeof suggestedQty === "number" ? (
                                        <span className="text-[10px] font-medium text-primary-red whitespace-nowrap">
                                            Suggested: {suggestedQty}
                                        </span>
                                    ) : null;
                                const cocktailBottleSize =
                                    it?.type?.type === "cocktails"
                                        ? getCocktailProduct(it.brand, numberOfCocktailsPerLine, it?.cocktails?.id).size
                                        : null;

                                return (
                                    <div
                                        key={it.id}
                                        className="flex w-full min-w-max max-w-none items-center gap-3 px-4 py-4"
                                    >
                                        {/* Name: one row = one brand; fills remaining width so price/actions align across rows */}
                                        <div className="min-w-0 flex-1 basis-0">
                                            <p className="flex flex-row items-center truncate text-sm font-semibold text-gray-900">
                                                {it?.type?.value == 'other' && it?.type?.name}
                                                {it?.type?.type === "cocktails" && it?.cocktails && (
                                                    <span>
                                                        {it?.brand?.name && (
                                                            <span className={isPreferredBrand(it.brand, preferredProducts) ? "text-primary-red" : ""}>
                                                                {it.brand.name}
                                                                {isPreferredBrand(it.brand, preferredProducts) && (
                                                                    <span className="ml-1 text-[10px]">(Preferred)</span>
                                                                )}
                                                            </span>
                                                        )}
                                                        {/* <span className="text-gray-500 font-normal">  {it.cocktails?.name}</span> */}
                                                    </span>
                                                )}
                                                {it?.type?.type === "products" && it?.brand?.name && (
                                                    <span className={isPreferredBrand(it.brand, preferredProducts) ? "text-primary-red" : ""}>
                                                        {it.brand.name}
                                                        {isPreferredBrand(it.brand, preferredProducts) && (
                                                            <span className="ml-1 text-[10px]">(Preferred)</span>
                                                        )}
                                                    </span>
                                                )}
                                                {it?.cocktails && it.cocktails?.value == "custom" && it?.brandName}
                                                {it?.provider?.value == "customer-provide" && (
                                                    <p className=" ml-1 text-[8px] text-gray-400 mt-[-8px]">
                                                        Client Provides
                                                    </p>
                                                )}
                                                {it?.provider?.value == "ho-provide" && (
                                                    <p className=" ml-1 text-[8px] text-gray-400 mt-[-8px]">
                                                        Selling Cocktails
                                                    </p>
                                                )}
                                            </p>
                                            {(isFirstInGroup && groupTotalDrinks != null
                                                ? true
                                                : getLineShareActualLabel(it) != null && (it?.type?.type !== "cocktails" || group.items.length === 1 || subIndex === 0)
                                            ) && (
                                                <p className="mt-0.5 text-[10px] text-gray-500">
                                                    {isFirstInGroup && groupTotalDrinks != null
                                                        ? `${groupTotalDrinks} drinks`
                                                        : getLineShareActualLabel(it)}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                                            <span className="inline-block w-[72px] shrink-0 text-right">
                                                {suggestedNode}
                                            </span>
                                            <span className="flex w-[124px] shrink-0 items-center gap-2">
                                                <span className="text-xs text-gray-400">Qty:</span>
                                                {onQtyChange ? (
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={10000}
                                                        value={editingQty?.index === index ? editingQty.value : String(it?.qty ?? "")}
                                                        onFocus={() => setEditingQty({ index, value: String(it?.qty ?? "") })}
                                                        onChange={(e) => {
                                                            if (editingQty?.index === index) {
                                                                setEditingQty((prev) => prev ? { ...prev, value: e.target.value } : null);
                                                            }
                                                        }}
                                                        onBlur={() => {
                                                            if (editingQty?.index === index && onQtyChange) {
                                                                const val = editingQty.value === "" ? 1 : parseInt(editingQty.value, 10);
                                                                const raw = Number.isFinite(val) ? val : 1;
                                                                const qty = Math.min(10000, Math.max(1, raw));
                                                                onQtyChange(index, qty);
                                                                setEditingQty(null);
                                                            }
                                                        }}
                                                        className="h-[36px] w-16 shrink-0 rounded-full bg-p-gray px-3 py-2 text-[14px] font-[400] text-[#000000] focus:outline-none focus:ring-1 focus:ring-primary-red"
                                                    />

                                                ) : (
                                                    <span className="text-xs text-gray-400">{it?.qty ?? "—"}</span>
                                                )}
                                            </span>
                                            <span
                                                className={`inline-block w-[92px] shrink-0 text-left text-xs text-gray-900 ${
                                                    it?.type?.type === "cocktails" && !cocktailBottleSize
                                                        ? "invisible"
                                                        : ""
                                                }`}
                                            >
                                                {it?.type?.type === "cocktails" && `${cocktailBottleSize} ml bottles`}
                                                {it?.type?.type === "products" && getProductType(it?.type?.value)}
                                            </span>
                                        </div>
                                        <div className="w-[110px] shrink-0 text-right">
                                            {it?.provider?.value === "customer-provide" || it?.provider?.value === "ho-provide" || it?.type?.value === "other" || it?.brand?.id === "other" ? (
                                                <span className="inline-block" aria-hidden />
                                            ) : (
                                                <p className="truncate text-sm font-semibold text-gray-900 tabular-nums">
                                                    ${cocktailPrice(it)}
                                                </p>
                                            )}
                                        </div>
                                        <div className="ml-2 inline-flex min-w-[4.5rem] shrink-0 items-center justify-end gap-2 text-gray-400">
                                            {showActions ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={handleRemove}
                                                        className="rounded-full bg-gray-100 p-[5px] text-rose-500"
                                                    >
                                                        <FiTrash2 className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onEdit(group.items[0].id)}
                                                        className="rounded-full bg-gray-100 p-[5px] hover:text-gray-700"
                                                    >
                                                        <FiEdit2 className="h-3 w-3" />
                                                    </button>
                                                </>
                                            ) : (
                                                <span className="inline-block w-[4.5rem]" aria-hidden />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </li>
                    );
                })}
            </ul>
        </div>
    );
}
